import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Button,
  IconButton,
  Switch,
  Tooltip,
  Snackbar,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import HistoryIcon from '@mui/icons-material/History';
import { IconRefresh, IconPlus } from '@tabler/icons-react';
import { SecondaryToolbar, ToolbarSpacer, ToolbarButton } from '../components/toolbar';
import {
  DataTableCard,
  DataTable,
  DataTableHeadCell,
  DataTableRow,
  DataTableCell,
  DataTableEmpty,
} from '../components/table';
import { Chip, toneFromMuiColor } from '../components/chip';
import type {
  RecurringTodo,
  RecurringTodoRun,
  CreateRecurringTodoRequest,
  UpdateRecurringTodoRequest,
} from '@techgitter/contracts';
import {
  fetchRecurringTodos,
  createRecurringTodo,
  updateRecurringTodo,
  deleteRecurringTodo,
  runRecurringTodo,
  fetchRecurringTodoRuns,
} from '../services/recurringTodosApi';
import { RecurringTodoPanel } from '../components/admin/RecurringTodoPanel';
import { priorityLabel, priorityColor, formatDate } from '../utils/todoDisplay';

/** Format a run's ISO timestamp as a compact local date + time. */
const formatRunTimestamp = (iso: string): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
};

export const RecurringTasksAdminPage: React.FC = () => {
  const [items, setItems] = useState<RecurringTodo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<RecurringTodo | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [dialogError, setDialogError] = useState<string | null>(null);

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Run Now
  const [runTarget, setRunTarget] = useState<RecurringTodo | null>(null);
  const [runAdvance, setRunAdvance] = useState(true);
  const [isRunning, setIsRunning] = useState(false);

  // Run history
  const [historyTarget, setHistoryTarget] = useState<RecurringTodo | null>(null);
  const [runs, setRuns] = useState<RecurringTodoRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [runsError, setRunsError] = useState<string | null>(null);

  useEffect(() => {
    if (!historyTarget) return;
    const controller = new AbortController();
    setRunsLoading(true);
    setRunsError(null);
    setRuns([]);
    fetchRecurringTodoRuns(historyTarget.id, controller.signal)
      .then(setRuns)
      .catch((err) => {
        if (err instanceof Error && err.name === 'AbortError') return;
        setRunsError(err instanceof Error ? err.message : 'Failed to load run history');
      })
      .finally(() => setRunsLoading(false));
    return () => controller.abort();
  }, [historyTarget]);

  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  const load = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true);
      setError(null);
      setItems(await fetchRecurringTodos(signal));
    } catch (err) {
      if (err instanceof Error && err.name === 'AbortError') return;
      setError(err instanceof Error ? err.message : 'Failed to load recurring tasks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  const handleCreate = () => {
    setEditing(null);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleEdit = (rec: RecurringTodo) => {
    setEditing(rec);
    setDialogError(null);
    setDialogOpen(true);
  };

  const handleSave = async (payload: CreateRecurringTodoRequest | UpdateRecurringTodoRequest) => {
    setIsSaving(true);
    setDialogError(null);
    try {
      if (editing) {
        await updateRecurringTodo(editing.id, payload);
      } else {
        await createRecurringTodo(payload as CreateRecurringTodoRequest);
      }
      setDialogOpen(false);
      setEditing(null);
      setSnackbar({ open: true, message: editing ? 'Recurring task updated' : 'Recurring task created', severity: 'success' });
      load();
    } catch (err) {
      setDialogError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleEnabled = async (rec: RecurringTodo) => {
    const previous = rec.enabled;
    setItems((cur) => cur.map((r) => (r.id === rec.id ? { ...r, enabled: !r.enabled } : r)));
    try {
      await updateRecurringTodo(rec.id, { enabled: !previous });
    } catch (err) {
      setItems((cur) => cur.map((r) => (r.id === rec.id ? { ...r, enabled: previous } : r)));
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to update', severity: 'error' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirmId) return;
    setIsDeleting(true);
    try {
      await deleteRecurringTodo(deleteConfirmId);
      setItems((cur) => cur.filter((r) => r.id !== deleteConfirmId));
      setDeleteConfirmId(null);
      setSnackbar({ open: true, message: 'Recurring task deleted', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to delete', severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRunClick = (rec: RecurringTodo) => {
    setRunTarget(rec);
    setRunAdvance(true);
  };

  const handleRunConfirm = async () => {
    if (!runTarget) return;
    setIsRunning(true);
    try {
      const updated = await runRecurringTodo(runTarget.id, runAdvance);
      setItems((cur) => cur.map((r) => (r.id === updated.id ? updated : r)));
      const noun = updated.outputKind === 'ticket' ? 'ticket' : 'to-do';
      const suffix = runAdvance ? ` — next run ${formatDate(updated.nextRun)}` : '';
      setSnackbar({
        open: true,
        message: `Generated ${noun} for “${updated.title}”${suffix}`,
        severity: 'success',
      });
      setRunTarget(null);
    } catch (err) {
      setSnackbar({ open: true, message: err instanceof Error ? err.message : 'Failed to run', severity: 'error' });
    } finally {
      setIsRunning(false);
    }
  };

  const deleteTarget = items.find((r) => r.id === deleteConfirmId);

  return (
    <>
      <SecondaryToolbar floating>
          <ToolbarSpacer />
          <ToolbarButton icon={IconRefresh} onClick={() => load()}>
            Refresh
          </ToolbarButton>
          <ToolbarButton variant="primary" icon={IconPlus} onClick={handleCreate}>
            Add Recurring Task
          </ToolbarButton>
      </SecondaryToolbar>

      {/* Two-column layout — content left, panel right. Collapses to a single
          column when the panel is closed. */}
      <div className={`recurring-layout ${dialogOpen ? 'recurring-layout--panel-open' : 'recurring-layout--panel-closed'}`}>
      <Box sx={{ p: '12px 16px 24px', minWidth: 0 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <DataTableCard>
          <DataTable>
            <thead>
              <tr>
                <DataTableHeadCell>Title</DataTableHeadCell>
                <DataTableHeadCell>Frequency</DataTableHeadCell>
                <DataTableHeadCell>Next Run</DataTableHeadCell>
                <DataTableHeadCell>End Date</DataTableHeadCell>
                <DataTableHeadCell>Priority</DataTableHeadCell>
                <DataTableHeadCell>Assignee</DataTableHeadCell>
                <DataTableHeadCell>Customer</DataTableHeadCell>
                <DataTableHeadCell>Enabled</DataTableHeadCell>
                <DataTableHeadCell align="right">Actions</DataTableHeadCell>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <DataTableEmpty colSpan={9}>No recurring tasks</DataTableEmpty>
              ) : (
                items.map((rec) => (
                  <DataTableRow key={rec.id}>
                    <DataTableCell bold>{rec.title}</DataTableCell>
                    <DataTableCell>{rec.frequency}</DataTableCell>
                    <DataTableCell>{formatDate(rec.nextRun)}</DataTableCell>
                    <DataTableCell>{formatDate(rec.endDate)}</DataTableCell>
                    <DataTableCell>
                      <Chip tone={toneFromMuiColor(priorityColor(rec.priority))}>
                        {priorityLabel(rec.priority)}
                      </Chip>
                    </DataTableCell>
                    <DataTableCell>{rec.assignedToName || '—'}</DataTableCell>
                    <DataTableCell>{rec.customer?.name || '—'}</DataTableCell>
                    <DataTableCell>
                      <Switch checked={rec.enabled} onChange={() => handleToggleEnabled(rec)} size="small" />
                    </DataTableCell>
                    <DataTableCell align="right">
                      <Tooltip title="Run now">
                        <IconButton size="small" onClick={() => handleRunClick(rec)}>
                          <PlayArrowIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Run history">
                        <IconButton size="small" onClick={() => setHistoryTarget(rec)}>
                          <HistoryIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(rec)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" onClick={() => setDeleteConfirmId(rec.id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </DataTableCell>
                  </DataTableRow>
                ))
              )}
            </tbody>
          </DataTable>
        </DataTableCard>
      )}
      </Box>

      {dialogOpen && (
        <div className="recurring-panel-col">
          <RecurringTodoPanel
            recurring={editing}
            isSaving={isSaving}
            error={dialogError}
            onClose={() => {
              if (!isSaving) {
                setDialogOpen(false);
                setEditing(null);
                setDialogError(null);
              }
            }}
            onSave={handleSave}
          />
        </div>
      )}
      </div>

      <Dialog open={historyTarget !== null} onClose={() => setHistoryTarget(null)} maxWidth="md" fullWidth>
        <DialogTitle>Run history — {historyTarget?.title}</DialogTitle>
        <DialogContent>
          {runsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : runsError ? (
            <Alert severity="error">{runsError}</Alert>
          ) : (
            <DataTable>
              <thead>
                <tr>
                  <DataTableHeadCell>Ran At</DataTableHeadCell>
                  <DataTableHeadCell>Trigger</DataTableHeadCell>
                  <DataTableHeadCell>Advanced</DataTableHeadCell>
                  <DataTableHeadCell>Kind</DataTableHeadCell>
                  <DataTableHeadCell>Status</DataTableHeadCell>
                  <DataTableHeadCell>Outcome</DataTableHeadCell>
                </tr>
              </thead>
              <tbody>
                {runs.length === 0 ? (
                  <DataTableEmpty colSpan={6}>No runs yet</DataTableEmpty>
                ) : (
                  runs.map((run) => (
                    <DataTableRow key={run.id}>
                      <DataTableCell>{formatRunTimestamp(run.ranAt)}</DataTableCell>
                      <DataTableCell>{run.trigger === 'manual' ? 'Run now' : 'Scheduled'}</DataTableCell>
                      <DataTableCell>{run.advanced ? 'Yes' : 'No'}</DataTableCell>
                      <DataTableCell>{run.outputKind === 'ticket' ? 'Ticket' : 'To-do'}</DataTableCell>
                      <DataTableCell>
                        <Chip tone={toneFromMuiColor(run.status === 'success' ? 'success' : 'error')}>
                          {run.status}
                        </Chip>
                      </DataTableCell>
                      <DataTableCell>
                        {run.status === 'failed' ? (run.error || 'Failed') : (run.outcomeRef || '—')}
                      </DataTableCell>
                    </DataTableRow>
                  ))
                )}
              </tbody>
            </DataTable>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryTarget(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={runTarget !== null} onClose={isRunning ? undefined : () => setRunTarget(null)}>
        <DialogTitle>Run Now</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Generate {runTarget?.outputKind === 'ticket' ? 'a ticket' : 'a to-do'} for &quot;{runTarget?.title}&quot; right now, without waiting for the schedule.
          </DialogContentText>
          <FormControlLabel
            sx={{ mt: 1 }}
            control={
              <Checkbox
                checked={runAdvance}
                onChange={(e) => setRunAdvance(e.target.checked)}
              />
            }
            label="Advance the schedule to the next occurrence"
          />
          <DialogContentText sx={{ mt: 0.5, fontSize: '0.8125rem' }}>
            {runAdvance
              ? `Next Run moves forward as if this were the scheduled run${runTarget ? ` (currently ${formatDate(runTarget.nextRun)})` : ''}.`
              : 'Ad-hoc extra run — the schedule is left unchanged and the next scheduled run still fires.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunTarget(null)} disabled={isRunning}>Cancel</Button>
          <Button onClick={handleRunConfirm} variant="contained" disabled={isRunning}>
            {isRunning ? 'Running…' : 'Run now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmId !== null} onClose={isDeleting ? undefined : () => setDeleteConfirmId(null)}>
        <DialogTitle>Delete Recurring Task</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete &quot;{deleteTarget?.title}&quot;? Existing generated tasks are not affected.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmId(null)} disabled={isDeleting}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" disabled={isDeleting}>
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};
